/**
 * Main application sidebar.
 *
 * Layout matches `ianfiles/sidebar-layout.md`:
 *
 *   - Header: "OpenSupply" wordmark + sub-line.
 *   - Body:   nav groups (Overview is always open; the others are
 *             Collapsible groups that default to open).
 *   - Footer: avatar with presence dot + name + role + settings cog.
 *             Clicking the avatar opens a popover with status / profile /
 *             docs / share-bundles walkthrough / support / sign out.
 *
 * The Vendor Portal group is only shown to vendor or both-type orgs (this
 * gate falls back to "always show" if the user/org shape doesn't expose
 * `orgType` yet — the toggle is documented in the design spec).
 */
import { Link, useLocation } from "wouter";
import { useState } from "react";
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  ShoppingCart,
  Truck,
  Settings,
  Building2,
  FileCheck,
  ClipboardList,
  Receipt,
  UserPlus,
  ListOrdered,
  Warehouse,
  FileInput,
  ClipboardCheck,
  ChevronDown,
  PackageCheck,
  FileUp,
  BookOpen,
  HelpCircle,
  LogOut,
  Send,
  Share2,
  User as UserIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAuth } from "@/lib/auth-context";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/cn";

type MainNavItem = { title: string; url: string; icon: typeof LayoutDashboard };

const overviewItems: MainNavItem[] = [
  { title: "Dashboard", url: "/", icon: LayoutDashboard },
  { title: "Products", url: "/products", icon: Package },
];

const salesItems: MainNavItem[] = [
  { title: "Customers", url: "/accounts", icon: Building2 },
  { title: "Customer POs", url: "/customer-pos", icon: FileUp },
  { title: "Orders", url: "/orders", icon: ShoppingCart },
  { title: "Line Items", url: "/order-line-items", icon: ListOrdered },
  { title: "Sales Quotes", url: "/quotes", icon: FileText },
  { title: "Invoices", url: "/invoices", icon: Receipt },
];

const crmItems: MainNavItem[] = [
  { title: "Leads", url: "/leads", icon: UserPlus },
  { title: "Opportunities", url: "/opportunities", icon: FileCheck },
  { title: "Contacts", url: "/contacts", icon: Users },
];

const procurementItems: MainNavItem[] = [
  { title: "Vendors", url: "/vendors", icon: Truck },
  { title: "Vendor Invoices", url: "/ap-invoices", icon: FileInput },
  { title: "Vendor Quotes", url: "/vendor-quotes", icon: FileText },
  { title: "Purchase Orders", url: "/purchase-orders", icon: ClipboardList },
  { title: "Goods Receipts", url: "/goods-receipts", icon: ClipboardCheck },
];

const supplyChainItems: MainNavItem[] = [
  { title: "Inventory", url: "/inventory", icon: Warehouse },
  { title: "Shipments", url: "/shipments", icon: Truck },
];

const vendorPortalItems: MainNavItem[] = [
  { title: "Catalog Upload", url: "/vendor-portal/catalogs", icon: PackageCheck },
];

// ── Presence ────────────────────────────────────────────────────────────────

type PresenceStatus = "active" | "busy" | "ooo";

const PRESENCE_LABEL: Record<PresenceStatus, string> = {
  active: "Active",
  busy: "Busy",
  ooo: "Out of Office",
};

const PRESENCE_COLOR: Record<PresenceStatus, string> = {
  active: "bg-emerald-500",
  busy: "bg-amber-500",
  ooo: "bg-slate-400",
};

// ── Nav primitives ──────────────────────────────────────────────────────────

function NavGroup({
  label,
  items,
  isActive,
  setOpenMobile,
}: {
  label: string;
  items: MainNavItem[];
  isActive: (url: string) => boolean;
  setOpenMobile: (open: boolean) => void;
}) {
  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton asChild isActive={isActive(item.url)}>
                <Link href={item.url} onClick={() => setOpenMobile(false)}>
                  <item.icon className="h-4 w-4" />
                  <span>{item.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function CollapsibleNavGroup({
  label,
  items,
  isActive,
  setOpenMobile,
}: {
  label: string;
  items: MainNavItem[];
  isActive: (url: string) => boolean;
  setOpenMobile: (open: boolean) => void;
}) {
  return (
    <SidebarGroup>
      <Collapsible defaultOpen className="group/collapsible">
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger className="flex w-full items-center justify-between text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground">
            {label}
            <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link href={item.url} onClick={() => setOpenMobile(false)}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
}

// ── User popover (presence + profile + docs + share-bundles + support + sign-out) ──

function UserPopoverContent({
  status,
  setStatus,
  onNavigate,
  onLogout,
}: {
  status: PresenceStatus;
  setStatus: (s: PresenceStatus) => void;
  onNavigate: (url: string) => void;
  onLogout: () => void;
}) {
  return (
    <div className="w-60 p-1">
      <div className="px-2 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Status
      </div>
      <div className="space-y-0.5 px-1 pb-1">
        {(Object.keys(PRESENCE_LABEL) as PresenceStatus[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover-elevate active-elevate-2",
              status === s && "bg-sidebar-accent/40"
            )}
          >
            <span className={cn("h-2 w-2 rounded-full", PRESENCE_COLOR[s])} />
            <span className="flex-1 text-left">{PRESENCE_LABEL[s]}</span>
            {status === s && (
              <span className="text-xs text-muted-foreground">Current</span>
            )}
          </button>
        ))}
      </div>

      <div className="my-1 border-t" />

      <PopoverItem
        icon={UserIcon}
        label="Profile"
        onClick={() => onNavigate("/settings/profile")}
      />
      <PopoverItem
        icon={BookOpen}
        label="OpenSupply Docs"
        onClick={() => {
          window.open("https://docs.opensupply.example", "_blank", "noopener");
        }}
      />
      <PopoverItem
        icon={Share2}
        label="Share Bundles walkthrough"
        onClick={() => onNavigate("/share-bundles/walkthrough")}
      />
      <PopoverItem
        icon={HelpCircle}
        label="Support"
        onClick={() => {
          window.location.href = "mailto:support@opensupply.example";
        }}
      />

      <div className="my-1 border-t" />

      <PopoverItem icon={LogOut} label="Sign out" onClick={onLogout} danger />
    </div>
  );
}

function PopoverItem({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: typeof LayoutDashboard;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover-elevate active-elevate-2",
        danger && "text-destructive"
      )}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </button>
  );
}

// ── Sidebar ─────────────────────────────────────────────────────────────────

export function AppSidebar() {
  const [location, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const { setOpenMobile } = useSidebar();
  const [presence, setPresence] = useState<PresenceStatus>("active");
  const [popoverOpen, setPopoverOpen] = useState(false);

  const isActive = (url: string) => {
    if (url === "/") return location === "/";
    return location.startsWith(url);
  };

  const handleSettingsClick = () => {
    setLocation("/settings");
    setOpenMobile(false);
  };

  const navigateFromPopover = (url: string) => {
    setPopoverOpen(false);
    setLocation(url);
    setOpenMobile(false);
  };

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "User";
  const initials =
    ((user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? "")).toUpperCase() ||
    (user?.email?.[0] ?? "?").toUpperCase();
  const roleLabel = user?.role ? humanizeRole(user.role) : "";

  // Vendor Portal visibility — show for vendor or both-type orgs. Until the
  // user/org shape exposes `orgType`, we fall back to showing it always so
  // the doc layout is preserved.
  const showVendorPortal = true;

  return (
    <Sidebar>
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex flex-col">
            <span className="text-xl font-semibold">OpenSupply</span>
            <span className="max-w-[140px] truncate text-xs text-muted-foreground">
              ERP System
            </span>
          </div>
        </div>
      </SidebarHeader>

      <div className="flex-1 overflow-auto px-2">
        <NavGroup
          label="Overview"
          items={overviewItems}
          isActive={isActive}
          setOpenMobile={setOpenMobile}
        />
        <CollapsibleNavGroup
          label="Sales"
          items={salesItems}
          isActive={isActive}
          setOpenMobile={setOpenMobile}
        />
        <CollapsibleNavGroup
          label="CRM"
          items={crmItems}
          isActive={isActive}
          setOpenMobile={setOpenMobile}
        />
        <CollapsibleNavGroup
          label="Procurement"
          items={procurementItems}
          isActive={isActive}
          setOpenMobile={setOpenMobile}
        />
        <CollapsibleNavGroup
          label="Supply Chain"
          items={supplyChainItems}
          isActive={isActive}
          setOpenMobile={setOpenMobile}
        />
        {showVendorPortal && (
          <CollapsibleNavGroup
            label="Vendor Portal"
            items={vendorPortalItems}
            isActive={isActive}
            setOpenMobile={setOpenMobile}
          />
        )}
      </div>

      <SidebarFooter className="border-t border-sidebar-border p-2">
        <div className="flex items-center gap-1">
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="relative flex flex-1 items-center gap-3 overflow-hidden rounded-md p-1.5 text-left hover-elevate active-elevate-2"
                aria-label="User menu"
              >
                <div className="relative shrink-0">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className={cn(
                      "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-sidebar",
                      PRESENCE_COLOR[presence]
                    )}
                    aria-label={`Status: ${PRESENCE_LABEL[presence]}`}
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">{fullName}</span>
                  {roleLabel && (
                    <span className="truncate text-xs text-muted-foreground">
                      {roleLabel}
                    </span>
                  )}
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" align="start" className="p-0">
              <UserPopoverContent
                status={presence}
                setStatus={(s) => setPresence(s)}
                onNavigate={navigateFromPopover}
                onLogout={() => {
                  setPopoverOpen(false);
                  logout();
                }}
              />
            </PopoverContent>
          </Popover>

          <button
            type="button"
            onClick={handleSettingsClick}
            className="shrink-0 rounded-md p-2 text-muted-foreground hover-elevate active-elevate-2"
            aria-label="Settings"
          >
            <Settings className="h-4 w-4" />
          </button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}

function humanizeRole(role: string): string {
  const r = role.toLowerCase();
  if (r === "superadmin" || r === "super-admin" || r === "super_admin") return "Super Admin";
  if (r === "admin" || r === "owner") return "Admin";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

// Silence unused-import warnings for icons reserved for nav items that aren't
// yet enabled (e.g. forecasts/pricing/etc. previously had entries — those
// were intentionally removed to match the doc spec, but keep the import so
// future toggles remain a one-line change).
void Send;
